import { useState } from 'react';

import type { WizardStepData } from '../types/wizard';

interface UseWizardStateProps {
  initialData?: WizardStepData;
  initialStep?: number;
  isAdminMode?: boolean;
}

/**
 * useWizardState Hook
 *
 * Manages wizard form state including:
 * - Active step tracking
 * - Wizard data across all steps
 * - Highest visited step (navigation restriction)
 * - API names toggle
 * - Admin mode (bypasses navigation restrictions)
 *
 * Based on wizardFormLoader.js and wizardForm.js state management
 */
export const useWizardState = ({
  initialData = {},
  initialStep = 0,
  isAdminMode = false,
}: UseWizardStateProps = {}) => {
  const [activeStep, setActiveStep] = useState(initialStep);
  const [wizardData, setWizardData] = useState<WizardStepData>(initialData);
  const [highestStep, setHighestStep] = useState(initialStep);
  const [showApiNames, setShowApiNames] = useState(false);
  const [adminMode, setAdminMode] = useState(isAdminMode);

  /**
   * Update wizard data (merge with existing)
   */
  const updateData = (stepData: Partial<WizardStepData>) => {
    setWizardData((prev) => ({ ...prev, ...stepData }));
  };

  /**
   * Advance to next step and update highestStep if needed
   * Based on wizardForm.js nextPage()
   */
  const nextStep = () => {
    const next = activeStep + 1;
    setActiveStep(next);
    if (next > highestStep) {
      setHighestStep(next);
    }
  };

  /**
   * Go back to previous step
   */
  const previousStep = () => {
    setActiveStep((prev) => Math.max(0, prev - 1));
  };

  /**
   * Navigate to specific step
   * Admin mode: can navigate to any step
   * Normal mode: can only navigate to previously visited steps (highestStep)
   * Based on wizardForm.js goToPage() - security check
   */
  const goToStep = (step: number) => {
    if (adminMode) {
      // Admin can jump to any step
      setActiveStep(step);
    } else if (step <= highestStep && step >= 0) {
      // Normal users: only previously visited steps
      setActiveStep(step);
    }
  };

  /**
   * Toggle between API names and friendly names
   * Based on wizardFormLoader.js showApiNames state
   */
  const toggleApiNames = () => {
    setShowApiNames((prev) => !prev);
  };

  /**
   * Toggle admin mode (bypasses navigation restrictions)
   */
  const toggleAdminMode = () => {
    setAdminMode((prev) => !prev);
  };

  /**
   * Reset wizard state (useful for clearing after completion)
   */
  const reset = () => {
    setActiveStep(0);
    setWizardData({});
    setHighestStep(0);
    setShowApiNames(false);
    setAdminMode(false);
  };

  return {
    // State
    activeStep,
    wizardData,
    highestStep,
    showApiNames,
    adminMode,

    // Actions
    updateData,
    nextStep,
    previousStep,
    goToStep,
    toggleApiNames,
    toggleAdminMode,
    reset,
  };
};
